import React from 'react';

import { GraphqlContextValues } from '@sbu/contexts.graphql';
import {
  ENTITY_REPUTATIONS,
  getReputationText,
} from '@sbu/views.entityindicator';

import { EntityCriticalityConfig, SearchResultType } from 'core/graphql';

import {
  booleanFieldDefaults,
  FieldTypes,
  keyValueFieldDefaults,
} from '../types/fieldTypes';
import {
  globalOperators,
  nullOperator,
  numericOperators,
  stringOperators,
  textOperators,
} from '../types/operatorTypes';
import { getEntityTypeFilters } from '../utils';
import { autocompleteQuery } from './gql';

export const entityTypeOptions = [
  {
    label: 'Command',
    key: '_command',
    prop: '_command',
  },
  {
    label: 'Domain',
    key: '_domain',
    prop: '_domain',
  },
  {
    label: 'Email',
    key: '_email',
    prop: '_email',
  },
  {
    label: 'File',
    key: '_file',
    prop: '_file',
  },
  {
    label: 'Hash',
    key: '_hash',
    prop: '_hash',
  },
  {
    label: 'Hostname',
    key: '_hostname',
    prop: '_hostname',
  },
  {
    label: 'IP Address',
    key: '_ip',
    prop: '_ip',
  },
  {
    label: 'MAC Address',
    key: '_mac',
    prop: '_mac',
  },
  {
    label: 'Process',
    key: '_process',
    prop: '_process',
  },
  {
    label: 'URL',
    key: '_url',
    prop: '_url',
  },
  {
    label: 'User Agent',
    key: '_useragent',
    prop: '_useragent',
  },
  {
    label: 'Username',
    key: '_username',
    prop: '_username',
  },
];

export const entityDefaultFields = (
  graphqlContext: GraphqlContextValues,
  criticalityValues: Pick<EntityCriticalityConfig, 'name'>[]
): FieldTypes[] => [
  {
    label: 'Signal Severity Total',
    prop: 'recentSignalSeverity',
    operators: [...globalOperators, ...numericOperators],
    type: 'numeric',
    source: 'entity',
    min: 0,
  },
  {
    label: 'Activity Score',
    prop: 'activityScore',
    operators: [...globalOperators, ...numericOperators],
    type: 'numeric',
    source: 'entity',
    min: 0,
  },
  {
    label: 'Criticality',
    prop: 'criticality',
    operators: [...globalOperators],
    type: 'list',
    source: 'entity',
    options: [
      {
        prop: null,
        label: 'Default',
        listTemplate: (label) => <span>{label}</span>,
      },
      ...[...criticalityValues]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((criticality) => ({
          prop: criticality.name,
          label: criticality.name,
          group: 'Criticality Values',
          listTemplate: (label) => <span>{label}</span>,
        })),
    ],
  },
  {
    label: 'Indicator',
    prop: 'reputation',
    source: 'entity',
    type: 'list',
    operators: [...nullOperator, ...globalOperators],
    options: ENTITY_REPUTATIONS.map((reputation) => ({
      prop: reputation,
      label: getReputationText(reputation),
    })),
  },
  {
    label: 'Sensor Zone',
    prop: 'sensorZone',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Entities,
        'sensorZone',
        value,
        graphqlContext
      ),
    operators: [...globalOperators, ...textOperators],
    type: 'text',
    source: 'entity',
  },
  {
    ...booleanFieldDefaults,
    label: 'Suppressed',
    prop: 'whitelisted',
    source: 'entity',
  },
  {
    ...keyValueFieldDefaults,
    label: 'Tags',
    prop: 'tag',
    source: 'entity',
  },
  {
    label: 'Type',
    prop: 'type',
    operators: [
      {
        label: 'is',
        type: 'equals',
      },
      {
        label: 'is not',
        type: 'doesNotEqual',
      },
    ],
    options: getEntityTypeFilters(),
    type: 'list',
    source: 'entity',
  },
  {
    label: 'Value',
    prop: 'value',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Entities,
        'value',
        value,
        graphqlContext
      ),
    operators: [...globalOperators, ...stringOperators],
    source: 'entity',
    type: 'text',
  },
];
